import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import "./ApprovalAdmin.css";

const ADMIN_HEADERS = {
  "x-admin-username": "Alumini2026",
  "x-admin-password": "Alu26@"
};

const ApprovalAdmin = () => {
  const [applications, setApplications] = useState([]);
  const [loadingId, setLoadingId] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const getRequestConfig = () => ({
    headers: ADMIN_HEADERS
  });

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setErrorMessage("");
      const res = await api.get("/api/admin/applications", getRequestConfig());
      setApplications(Array.isArray(res.data?.applications) ? res.data.applications : []);
    } catch (error) {
      console.error("Load applications error:", error);
      setErrorMessage(error?.response?.data?.message || "Failed to load applications");
    }
  };

  const getRecordId = (person) => person?._id || person?.id || "";

  const normalizePhoneNumber = (phone) => {
    if (!phone) return "";
    let cleaned = String(phone).replace(/\D/g, "");
    if (cleaned.length === 10) cleaned = `91${cleaned}`;
    return cleaned;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-GB");
  };

  const openWhatsAppMessage = (phone, message) => {
    if (!phone) return;
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const messageTemplates = {
    verification: (applicant) => `
Alumni Application Update

Dear ${applicant.name},

Thank you for registering with our official alumni association.

This is to confirm that we have received your application and it is currently under verification by our administrative team.

Application Reference: ${applicant.alumniId || applicant._id}
Course: ${applicant.course}
Batch: ${applicant.batchYear}

We appreciate your patience. You will be notified once the review process is completed.

Regards,
Alumni Relations Team
CIT Coimbatore
`.trim(),

    approved: (applicant) => `
Application Approved

Dear ${applicant.name},

Congratulations. Your alumni application has been successfully verified and approved.

We are delighted to welcome you to the alumni community.

Permanent Alumni ID: ${applicant.alumniId}
Course: ${applicant.course}
Batch: ${applicant.batchYear}

You will receive the membership fee payment link shortly.

Warm regards,
Alumni Relations Team
CIT Coimbatore
`.trim(),

    rejected: (applicant) => `
Application Status Update

Dear ${applicant.name},

Thank you for applying for alumni membership.

After careful review, we regret to inform you that your application could not be approved at this time.

If you believe this requires clarification, please contact the alumni administration team.

Regards,
Alumni Relations Team
CIT Coimbatore
`.trim()
  };

  const handleStatusAction = async (applicant, action) => {
    const recordId = getRecordId(applicant);
    if (!recordId) return;

    try {
      setLoadingId(recordId);
      setSuccessMessage("");
      setErrorMessage("");

      const res = await api.patch(
        `/api/admin/applications/${recordId}/action`,
        { action },
        getRequestConfig()
      );

      const updatedRecord = res.data?.application || applicant;

      const phone = normalizePhoneNumber(updatedRecord.mobile);
      if (phone) {
        const message = messageTemplates[action](updatedRecord);
        openWhatsAppMessage(phone, message);
      }

      await loadApplications();

      if (action === "verification") {
        setSuccessMessage(`✅ Verification message sent to ${updatedRecord.name}`);
      } else if (action === "approved") {
        setSuccessMessage(`✅ ${updatedRecord.name} approved and moved to approved alumni list`);
      } else if (action === "rejected") {
        setSuccessMessage(`✅ Rejection message sent to ${updatedRecord.name}`);
      }

      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Status update error:", error);
      setErrorMessage(error?.response?.data?.message || "Failed to update application status");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setLoadingId("");
    }
  };

  const getStatusBadge = (status) => {
    const badgeClass = {
      Pending: "grey",
      "Under Verification": "yellow",
      Approved: "green",
      Rejected: "red"
    }[status] || "grey";

    return <span className={`status-badge ${badgeClass}`}>{status || "Pending"}</span>;
  };

  return (
    <div className="approval-admin">
      <div className="dashboard-header">
        <div>
          <h1>Application Review Dashboard</h1>
          <p>Review newly registered alumni applications and communicate status updates professionally.</p>
        </div>

        <div className="header-actions">
          <Link to="/admin/approved" className="btn btn-outline">
            View Approved Alumni
          </Link>
          <button onClick={loadApplications} className="btn btn-outline">
            🔄 Refresh List
          </button>
        </div>
      </div>

      {successMessage && <div className="success-banner">{successMessage}</div>}
      {errorMessage && <div className="error-banner">{errorMessage}</div>}

      <div className="table-container">
        <table className="alumni-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Course</th>
              <th>Batch</th>
              <th>Applied Date</th>
              <th>Current Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {applications.length === 0 ? (
              <tr>
                <td colSpan={8} className="no-data">
                  ✅ No applications currently waiting for review
                </td>
              </tr>
            ) : (
              applications.map((person) => {
                const recordId = getRecordId(person);
                const rowStatusClass = String(person.status || "pending")
                  .toLowerCase()
                  .replace(/\s+/g, "-");

                return (
                  <tr key={recordId} className={`row-status-${rowStatusClass}`}>
                    <td><strong>{person.name}</strong></td>
                    <td>{person.email}</td>
                    <td>{person.mobile}</td>
                    <td>{person.course}</td>
                    <td>{person.batchYear}</td>
                    <td>{formatDate(person.createdAt)}</td>
                    <td>{getStatusBadge(person.status)}</td>
                    <td>
                      <div className="action-button-group">
                        <button
                          className="btn btn-warning btn-xs"
                          onClick={() => handleStatusAction(person, "verification")}
                          disabled={loadingId === recordId}
                        >
                          {loadingId === recordId ? "Please wait..." : "Under Verification"}
                        </button>

                        <button
                          className="btn btn-success btn-xs"
                          onClick={() => handleStatusAction(person, "approved")}
                          disabled={loadingId === recordId}
                        >
                          {loadingId === recordId ? "Please wait..." : "Approve"}
                        </button>

                        <button
                          className="btn btn-danger btn-xs"
                          onClick={() => handleStatusAction(person, "rejected")}
                          disabled={loadingId === recordId}
                        >
                          {loadingId === recordId ? "Please wait..." : "Reject"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ApprovalAdmin;